import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { PDFViewer } from "@react-pdf/renderer";
import OldPurchasePDF from "../components/OldPurchasePDF";
import PurchaseAgreementPDF from "../components/PurchaseAgreementPDF";
import { toast } from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { listOldMetalPurchases, showOldMetalPurchaseById } from "../erpClient";
import {
  normalizeOldMetalListRows,
  isOldMetalApiSuccess,
  pickOldMetalShowDocument,
  mapOldMetalShowToLegacyCached,
} from "../utils/oldMetalPurchaseErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";

function MetalPurchaseTable() {
  const navigate = useNavigate();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [purchases, setPurchases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPurchaseForPdf, setSelectedPurchaseForPdf] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [showPurchaseAgreementModal, setShowPurchaseAgreementModal] =
    useState(false);
  const [selectedPurchaseForAgreement, setSelectedPurchaseForAgreement] =
    useState(null);
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false);
  const [purchaseDetailsCache, setPurchaseDetailsCache] = useState({});
  const [canAddOldPurchase, setCanAddOldPurchase] = useState(() =>
    hasPermission(PERMISSIONS.OLD_METAL_PURCHASE_ADD),
  );
  const [canEditOldPurchase, setCanEditOldPurchase] = useState(() =>
    hasPermission(PERMISSIONS.OLD_METAL_PURCHASE_EDIT),
  );

  useEffect(() => {
    const syncPerms = () => {
      setCanAddOldPurchase(hasPermission(PERMISSIONS.OLD_METAL_PURCHASE_ADD));
      setCanEditOldPurchase(hasPermission(PERMISSIONS.OLD_METAL_PURCHASE_EDIT));
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return as-is if invalid
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const pageCache = useVisitedPageCache();

  const fetchPurchases = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setPurchases(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await listOldMetalPurchases({
          search,
          page,
          limit,
        });

        if (!isOldMetalApiSuccess(response)) {
          setPurchases([]);
          setTotalCount(0);
          return;
        }

        let list = normalizeOldMetalListRows(response);
        list = list.map((item) => ({
          ...item,
          date: formatDate(item.date || ""),
        }));

        list.sort((a, b) => Number(b.id) - Number(a.id));
        const total = parseApiPagination(response, list.length).total;
        setPurchases(list);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: list, total });
      } catch (error) {
        console.error("Error fetching old purchases:", error);
        const status = error?.response?.status;
        if (status === 403) toast.error("No permission");
        else
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to fetch old purchase data",
          );
        setPurchases([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchPurchases(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchPurchases]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in MetalPurchaseTable, refetching purchases...",
        event.detail,
      );
      setCurrentPage(1);
      fetchPurchases(1, itemsPerPage, searchQuery, true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchPurchases]);

  // Socket connection and event listeners
  useEffect(() => {
    const socket = createErpSocket();

    // Connection event logging
    socket.on("connect", () => {
      // console.log("MetalPurchaseTable: Connected to socket server");
    });

    socket.on("disconnect", (reason) => {
      // console.log(
      //   "MetalPurchaseTable: Disconnected from socket server:",
      //   reason,
      // );
    });

    socket.on("connect_error", (error) => {
      console.error("MetalPurchaseTable: Socket connection error:", error);
    });

    const refreshOldMetal = () =>
      fetchPurchases(currentPage, itemsPerPage, searchQuery, true);

    socket.on("old_purchase_created", refreshOldMetal);
    socket.on("old_purchase_updated", refreshOldMetal);
    socket.on("old_metal_purchase_created", refreshOldMetal);
    socket.on("old_metal_purchase_updated", refreshOldMetal);

    // Listen for bank account updates (might affect user balances)
    socket.on("bank_account_updated", (data) => {
      // console.log("Bank account updated:", data);
    });

    // Listen for user updates (balance changes)
    socket.on("user_updated", (data) => {
      // console.log("User updated:", data);
    });

    return () => {
      socket.off("old_purchase_created");
      socket.off("old_purchase_updated");
      socket.off("old_metal_purchase_created");
      socket.off("old_metal_purchase_updated");
      socket.off("bank_account_updated");
      socket.off("user_updated");
      socket.disconnect();
    };
  }, [currentPage, itemsPerPage, searchQuery, fetchPurchases]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Fetch purchase data for PDF preview
  const handleViewPdf = async (purchaseId) => {
    setIsLoadingPdf(true);
    try {
      let cached = purchaseDetailsCache[purchaseId];
      if (!cached) {
        const response = await showOldMetalPurchaseById({ id: purchaseId });
        if (!isOldMetalApiSuccess(response)) {
          toast.error(response?.message || "Failed to load purchase data");
          return;
        }
        const doc = pickOldMetalShowDocument(response);
        cached = mapOldMetalShowToLegacyCached(doc);
        if (!cached) {
          toast.error("Purchase data not found");
          return;
        }
        setPurchaseDetailsCache((prev) => ({ ...prev, [purchaseId]: cached }));
      }
      const purchaseDetails = cached.purchase_details || {};
      const purchaseItems = cached.purchase_items || [];
      const payments = cached.payments || [];
      const formatDateForPdf = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      const formattedData = {
        invoiceNumber: purchaseDetails.purchase_no || "",
        date: formatDateForPdf(
          purchaseDetails.date || purchaseDetails.bill_date,
        ),
        customer: {
          name: purchaseDetails.user_name || "",
          mobile: purchaseDetails.user_mobile || "",
          city: "AHMEDABAD",
        },
        items: purchaseItems.map((item) => ({
          item_name: item.item_name || "",
          pcs: item.pcs || 0,
          ct: item.ct || 0,
          gwt: item.gwt || 0,
          lwt: item.lwt || 0,
          nwt: item.nwt || 0,
          touch: item.touch || 0,
          wastage: item.wastage || 0,
          fine_wt: item.fine_wt || 0,
          total_amt: item.total_amt || 0,
        })),
        payments: payments.map((pmt) => ({
          payment_mode: pmt.payment_mode || "",
          bank_name: pmt.bank_name || "",
          amount: pmt.amount || 0,
          remarks: pmt.remarks || "",
        })),
        round_off: purchaseDetails.round_off || 0,
        advancePayment: purchaseDetails.advance_payment || 0,
        note: purchaseDetails.note || "",
        previousAmount: purchaseDetails.previous_amount || "",
        previousGold: purchaseDetails.previous_gold || "0",
        outstandingGold: purchaseDetails.outstanding_gold || "0.00",
      };
      setSelectedPurchaseForPdf(formattedData);
      setShowPdfModal(true);
    } catch (error) {
      console.error("Error fetching purchase data:", error);
      toast.error("Failed to load purchase data");
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Fetch purchase data for Purchase Agreement PDF
  const handleViewPurchaseAgreement = async (purchaseId) => {
    setIsLoadingAgreement(true);
    try {
      let cached = purchaseDetailsCache[purchaseId];
      if (!cached) {
        const response = await showOldMetalPurchaseById({ id: purchaseId });
        if (!isOldMetalApiSuccess(response)) {
          toast.error(response?.message || "Failed to load purchase data");
          return;
        }
        const doc = pickOldMetalShowDocument(response);
        cached = mapOldMetalShowToLegacyCached(doc);
        if (!cached) {
          toast.error("Purchase data not found");
          return;
        }
        setPurchaseDetailsCache((prev) => ({ ...prev, [purchaseId]: cached }));
      }
      const purchaseDetails = cached.purchase_details || {};
      const purchaseItems = cached.purchase_items || [];
      const formatDateForPdf = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      const formattedData = {
        invoiceNumber: purchaseDetails.purchase_no || "",
        date: formatDateForPdf(
          purchaseDetails.date || purchaseDetails.bill_date,
        ),
        customer: {
          name: purchaseDetails.user_name || "",
          mobile: purchaseDetails.user_mobile || "",
          city: "AHMEDABAD",
          address: "",
        },
        items: purchaseItems.map((item) => ({
          item_name: item.item_name || "",
          pcs: item.pcs || 0,
          ct: item.ct || 0,
          gwt: item.gwt || 0,
          lwt: item.lwt || 0,
          nwt: item.nwt || 0,
          touch: item.touch || 0,
          wastage: item.wastage || 0,
          fine_wt: item.fine_wt || 0,
          total_amt: item.total_amt || 0,
          rate: item.rate || item.total_amt / (item.nwt || 1),
        })),
      };
      setSelectedPurchaseForAgreement(formattedData);
      setShowPurchaseAgreementModal(true);
    } catch (error) {
      console.error("Error fetching purchase data:", error);
      toast.error("Failed to load purchase data");
    } finally {
      setIsLoadingAgreement(false);
    }
  };

  // Memoize PDF content
  const pdfContent = useMemo(() => {
    if (!selectedPurchaseForPdf) return null;

    return (
      <PDFViewer width="100%" height="100%">
        <OldPurchasePDF purchaseData={selectedPurchaseForPdf} />
      </PDFViewer>
    );
  }, [selectedPurchaseForPdf]);

  // Memoize Purchase Agreement PDF content
  const purchaseAgreementPdfContent = useMemo(() => {
    if (!selectedPurchaseForAgreement) return null;

    return (
      <PDFViewer width="100%" height="100%">
        <PurchaseAgreementPDF purchaseData={selectedPurchaseForAgreement} />
      </PDFViewer>
    );
  }, [selectedPurchaseForAgreement]);

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Metal Purchase Table</h4>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-secondary ${buttonClass}`}
                onClick={() => navigate("/urdledger")}
              >
                Ledger
              </button>
              {canAddOldPurchase && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={!isCurrentFinancialYear}
                  title={
                    !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                  }
                  onClick={() => {
                    if (!isCurrentFinancialYear) return;
                    navigate("/addoldpurchase");
                  }}
                >
                  URD Invoice
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search by purchase no / name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Purchase No</th>
                    <th>Date</th>
                    <th>User Name</th>
                    <th>GWT</th>
                    <th>NWT</th>
                    <th>Fine</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : purchases.length > 0 ? (
                    purchases.map((p) => {
                      return (
                        <tr key={p.id}>
                          <td>{p.purchase_no}</td>
                          <td>{p.date}</td>
                          <td>
                            <div>{p.user_name || "N/A"}</div>
                            {p.user_mobile ? (
                              <div className="small text-muted">
                                {p.user_mobile}
                              </div>
                            ) : null}
                          </td>
                          <td>{p.total_gwt.toFixed(3)}</td>
                          <td>{p.total_nwt.toFixed(3)}</td>
                          <td>{p.total_fine.toFixed(3)}</td>
                          <td>₹{p.total_amt.toFixed(2)}</td>
                          <td>
                            <button
                              className={`btn btn-info btn-sm me-1 ${buttonClass}`}
                              onClick={() => handleViewPdf(p.id)}
                              title="View PDF"
                              disabled={isLoadingPdf}
                            >
                              <Icon
                                icon="solar:eye-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                            <button
                              className={`btn btn-success btn-sm me-1 ${buttonClass}`}
                              onClick={() => handleViewPurchaseAgreement(p.id)}
                              title="Purchase Agreement"
                              disabled={isLoadingAgreement}
                            >
                              <Icon
                                icon="solar:document-text-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                            {canEditOldPurchase && (
                              <button
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                onClick={() =>
                                  navigate("/editoldpurchase", {
                                    state: { purchaseId: p.id },
                                  })
                                }
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No purchases found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* PDF Modal */}
      <Modal
        show={showPdfModal}
        onHide={() => {
          setShowPdfModal(false);
          setSelectedPurchaseForPdf(null);
        }}
        size="xl"
        centered
        dialogClassName="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Old Purchase PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
          {isLoadingPdf ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : pdfContent ? (
            pdfContent
          ) : (
            <div className="text-center p-4">No PDF data available</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Purchase Agreement PDF Modal */}
      <Modal
        show={showPurchaseAgreementModal}
        onHide={() => {
          setShowPurchaseAgreementModal(false);
          setSelectedPurchaseForAgreement(null);
        }}
        size="xl"
        centered
        dialogClassName="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Purchase Agreement PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
          {isLoadingAgreement ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : purchaseAgreementPdfContent ? (
            purchaseAgreementPdfContent
          ) : (
            <div className="text-center p-4">No PDF data available</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MetalPurchaseTable;
